import { useState } from 'react';
import { getDocumentFields } from 'utils/helper';

const getMostChosenLetter = (answersMap) => {
  const letters = Object.values(answersMap);

  if (!letters.length) {
    return null;
  }

  const counts = letters.reduce((acc, letter) => {
    acc[letter] = (acc[letter] || 0) + 1;

    return acc;
  }, {});

  return Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
};

export const useAssessmentSection = ({ section, type, handleOnCTAClick }) => {
  const {
    title,
    contentModules,
    description,
  } = getDocumentFields(section, ['title', 'contentModules', 'description']);

  const questionsModule = contentModules?.[0];
  const resultsModule = contentModules?.[1];

  const { contentModules: questionModules } = getDocumentFields(
    questionsModule,
    ['contentModules'],
  );

  const questions = (questionModules || []).map((question) => {
    const {
      title: questionTitle,
      contentModules: answerModules,
    } = getDocumentFields(question, ['title', 'contentModules']);

    const answers = (answerModules || []).map((answer) => {
      const {
        title: answerTitle,
        subtitle,
      } = getDocumentFields(answer, ['title', 'subtitle']);

      return {
        title: answerTitle,
        letter: subtitle,
      };
    });

    return {
      title: questionTitle,
      answers,
    };
  });

  const { contentModules: resultModules } = getDocumentFields(
    resultsModule,
    ['contentModules'],
  );

  const results = (resultModules || []).map((result) => {
    const {
      title: resultTitle,
      subtitle,
      description: resultDescription,
      text,
      contentModules: suggestionData,
    } = getDocumentFields(result, ['title', 'subtitle', 'text', 'contentModules', 'description']);

    const {
      contentModules: suggestionsList,
    } = getDocumentFields(suggestionData[0], ['contentModules']);

    const linkData = getDocumentFields(suggestionData[1], ['buttonTitle', 'url', 'slug']);

    let percentage = {};

    if (resultDescription) {
      const [startRange, endRange] = resultDescription.split('-');

      percentage = {
        startRange: +startRange || 0,
        endRange: +endRange || 0,
      };
    }

    return {
      title: resultTitle,
      letter: subtitle,
      text,
      suggestions: suggestionsList,
      link: linkData,
      ...percentage,
    };
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answersMap, setAnswersMap] = useState({});
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [result, setResult] = useState(null);

  const totalQuestions = questions.length;
  const answeredCount = Object.keys(answersMap).length + 1;
  const currentQuestion = questions[currentIndex];
  const progress = totalQuestions ? (answeredCount / totalQuestions) * 100 + 1 : 1;
  const isCompleted = Boolean(result);

  const resolveResult = (finalAnswersMap) => {
    const mostChosenLetter = getMostChosenLetter(finalAnswersMap);

    if (!mostChosenLetter) {
      return results[0] || null;
    }

    return results.find(({ letter }) => letter === mostChosenLetter) || results[0] || null;
  };

  const handleSelectAnswer = (letter) => {
    setSelectedLetter(letter);
  };

  const handleNext = () => {
    if (!selectedLetter) {
      return;
    }

    const nextAnswersMap = {
      ...answersMap,
      [currentIndex]: selectedLetter,
    };

    setAnswersMap(nextAnswersMap);

    if (currentIndex >= totalQuestions - 1) {
      setResult(resolveResult(nextAnswersMap));

      return;
    }

    const nextIndex = currentIndex + 1;

    setCurrentIndex(nextIndex);
    setSelectedLetter(nextAnswersMap[nextIndex] || null);
  };

  const handleSkip = () => {
    const nextAnswersMap = {
      ...answersMap,
      [currentIndex]: '-',
    };

    // const nextAnswersMap = {...answersMap};

    // delete nextAnswersMap[currentIndex];
    setAnswersMap(nextAnswersMap);

    if (currentIndex >= totalQuestions - 1) {
      setResult(resolveResult(nextAnswersMap));

      return;
    }

    const nextIndex = currentIndex + 1;

    setCurrentIndex(nextIndex);
    setSelectedLetter(nextAnswersMap[nextIndex] || null);
  };

  const withPercentage = ['ai-engineering-transformation/assessment'].includes(type);

  return {
    type,
    title,
    description,
    contentModules,
    questions,
    currentQuestion,
    currentIndex,
    totalQuestions,
    answeredCount,
    progress,
    selectedLetter,
    isCompleted,
    result,
    handleSelectAnswer,
    handleNext,
    handleSkip,
    handleOnCTAClick,
    withPercentage,
  };
};
